import { getEncounterTimelineData } from 'app/frontend/base/services/raidPlanData'
import {
  PlanAbilityDefaults,
  resolveCastReminderText,
  resolveCastRgb,
  resolveCastTtsMessage,
} from '../../TeamPlans/components/planAbilityDefaults'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import {
  raidMarkerTypes,
  RaidMarkerType,
} from 'app/frontend/types/custom/RaidMarker'
import i18n from 'i18next'
import {
  getAssignmentScopes,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getEffectiveAssignmentType,
  resolveAssignmentViewMode,
  usesNsrtInterruptModule,
  usesNsrtTransitionModule,
  getCascadedReminderText,
  getCascadedReminderTts,
  getEntityCascadeReminders,
  getEffectiveExpectedEntities,
  getEffectiveReminderDuration,
  getEffectiveReminderOffset,
  getGroupCascadeReminders,
  getSoakPrefix,
  isGeneralAssignmentType,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
  normalizeCascadeReminders,
  roundToOneDecimal,
  toOrdinalWord,
} from '../../TeamPlans/components/assignmentTimeline'
import {
  resolvePhaseTiming,
  resolvePhaseIndexAtTime,
} from '../../TeamPlans/utils/phaseTiming'
import {
  formatNotePhaseNumber,
  toNotePhaseNumber,
} from '../../TeamPlans/utils/notePhaseNumbers'
import {
  EventActivityLinkedPlanData,
  EventActivityLinkedRaidPlanData,
} from '../hooks/useEventActivityLinkedPlanData'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import {
  ResolvedAssignmentEntity,
  ResolvedAssignmentParticipant,
} from './eventActivityTimelineResolver'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import { unavailableParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { htmlToPlainText } from 'app/frontend/base/helpers/htmlToPlainText'
import { gameVersionHas } from 'app/frontend/types/custom/GameVersion'

type TimedExportLine = {
  at: number
  line: string
}

export type AssignmentSummaryScope = {
  id?: string
  label: string
  groups: Array<{ name: string; tokens: string[] }>
}

export type AssignmentSummary = {
  title: string
  subtitle?: string
  assignmentGroups: number
  scopes: AssignmentSummaryScope[]
}

const raidMarkerIconIds: Record<RaidMarkerType, number> = {
  star: 1,
  circle: 2,
  diamond: 3,
  triangle: 4,
  moon: 5,
  square: 6,
  cross: 7,
  skull: 8,
}

export const formatNoteMarkerIcons = (text: string) =>
  text.replace(/\{([a-z]+)\}/g, (token, marker: string) => {
    if (!raidMarkerTypes.includes(marker as RaidMarkerType)) return token
    const iconId = raidMarkerIconIds[marker as RaidMarkerType]
    return `|TInterface\\TargetingFrame\\UI-RaidTargetingIcon_${iconId}:0|t`
  })

export const formatAssignmentSummaryLines = (
  summary: AssignmentSummary,
): string[] => {
  const header = summary.subtitle
    ? `${summary.title} (${summary.subtitle})`
    : summary.title
  const lines = [header]
  summary.scopes.forEach((scope) => {
    const groups = scope.groups.filter((group) => group.tokens.length > 0)
    if (!groups.length) return
    const prefix = scope.label ? `${scope.label}: ` : ''
    if (summary.assignmentGroups <= 1) {
      lines.push(`${prefix}${groups[0].tokens.join(', ')}`)
      return
    }
    groups.forEach((group, index) => {
      const body = `${group.name} - ${group.tokens.join(', ')}`
      lines.push(
        index === 0
          ? `${prefix}${body}`
          : `${' '.repeat(prefix.length)}${body}`,
      )
    })
  })
  return lines
}

const normalizeRealmChatName = (realmName: string) =>
  realmName.replaceAll(' ', '').replaceAll('-', '')

const formatTimeValue = (value: number) => {
  const rounded = roundToOneDecimal(Math.max(value, 0))
  if (!Number.isFinite(rounded)) return '0'
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
}

const normalizeRoleToken = (role: string) => {
  if (role === 'heal') return 'healer'
  if (role === 'dps') return 'damager'
  return role
}

const toEntityToken = (entity: ResolvedAssignmentEntity) => {
  if (entity.kind === 'ignored') return ''
  if (entity.kind === 'participant') return entity.participant.inGameName
  if (entity.sourceEntity.kind === 'everyone') return 'everyone'
  if (entity.sourceEntity.kind === 'group')
    return `group${entity.sourceEntity.group}`
  if (entity.sourceEntity.kind === 'role') {
    return normalizeRoleToken(entity.sourceEntity.role)
  }
  if (entity.sourceEntity.kind === 'collection') {
    if (entity.sourceEntity.collection === 'everyone') return 'everyone'
    if (entity.sourceEntity.collection === 'healers') return 'healer'
    if (entity.sourceEntity.collection === 'tanks') return 'tank'
    if (entity.sourceEntity.collection === 'dps') return 'damager'
    return entity.sourceEntity.collection
  }
  if (entity.sourceEntity.kind === 'by_role') {
    return normalizeRoleToken(entity.sourceEntity.role)
  }
  if (entity.sourceEntity.kind === 'member') return entity.sourceEntity.label
  return ''
}

const toEntitySummaryLabel = (entity: ResolvedAssignmentEntity) => {
  if (entity.kind === 'ignored' || entity.issue) return ''
  if (entity.kind === 'participant') return entity.participant.inGameName
  if (entity.sourceEntity.kind === 'everyone') {
    return i18n.t('dashboard.team.plans.assignments.entities.everyone')
  }
  if (entity.sourceEntity.kind === 'collection') {
    return i18n.t(
      `dashboard.team.plans.assignments.entities.collection.${entity.sourceEntity.collection}`,
    )
  }
  if (entity.sourceEntity.kind === 'group') {
    return i18n.t('dashboard.team.plans.assignments.entities.group', {
      count: entity.sourceEntity.group,
    })
  }
  if (entity.sourceEntity.kind === 'role') {
    const role = normalizeRoleToken(entity.sourceEntity.role)
    const byRoleKey = role === 'damager' ? 'dps' : role
    return i18n.t(
      `dashboard.team.plans.assignments.entities.by-role.${byRoleKey}`,
    )
  }
  if (entity.sourceEntity.kind === 'by_role') {
    return i18n.t(
      `dashboard.team.plans.assignments.entities.by-role.${entity.sourceEntity.role}`,
    )
  }
  if (entity.sourceEntity.kind === 'member') return entity.sourceEntity.label
  return ''
}

const getBossTimelineEntries = ({
  maxTime,
  phaseStarts,
  phaseEnds,
  phaseDurations,
  abilities,
}: {
  maxTime: number
  phaseStarts: number[]
  phaseEnds: number[]
  phaseDurations: number[]
  abilities: Record<
    string,
    {
      spell_id: number
      entries: Array<{ time: number; duration: number; phaseIndex: number }>
    }
  >
}) => {
  return Object.values(abilities).flatMap((ability) =>
    ability.entries.flatMap((entry) => {
      const phaseStart = phaseStarts[entry.phaseIndex] ?? 0
      const phaseEnd = phaseEnds[entry.phaseIndex] ?? maxTime
      const phaseDuration = phaseDurations[entry.phaseIndex]
      if (phaseDuration === undefined) return []
      if (entry.time > phaseDuration || entry.time < 0) return []
      const entryTime = Math.floor(phaseStart + entry.time)
      if (entryTime < phaseStart || entryTime >= phaseEnd) return []
      return [
        {
          spellId: ability.spell_id,
          time: entryTime,
        },
      ]
    }),
  )
}

const getNearestBossSpellId = (
  castAt: number,
  bossEntries: Array<{ spellId: number; time: number }>,
) => {
  const candidates = bossEntries
    .map((entry) => ({
      spellId: entry.spellId,
      delta: Math.abs(entry.time - castAt),
      time: entry.time,
    }))
    .filter((entry) => entry.delta <= 3)
    .sort((left, right) => {
      if (left.delta !== right.delta) return left.delta - right.delta
      if (left.time !== right.time) return left.time - right.time
      return left.spellId - right.spellId
    })
  return candidates[0]?.spellId
}

const toAssignmentEntityTokens = (entities: ResolvedAssignmentEntity[]) =>
  entities
    .filter((entity) => entity.kind !== 'ignored' && !entity.issue)
    .map(toEntityToken)
    .filter((token) => token.length > 0)

const toAssignmentSummaryTokens = (entities: ResolvedAssignmentEntity[]) =>
  entities
    .filter((entity) => entity.kind !== 'ignored' && !entity.issue)
    .map(toEntitySummaryLabel)
    .filter((token) => token.length > 0)

const toAssignmentCharacterNames = (entities: ResolvedAssignmentEntity[]) =>
  entities.flatMap((entity) => {
    if (entity.kind !== 'participant' || entity.issue) return []
    return entity.participant.inGameName ? [entity.participant.inGameName] : []
  })

const toAssignmentReminderSlots = (
  entities: ResolvedAssignmentEntity[],
  expected?: number,
) => {
  const slots = entities.map((entity) => {
    if (entity.kind === 'ignored' || entity.issue) return ''
    return toEntityToken(entity)
  })
  const expectedCount = expected ?? 0
  while (slots.length < expectedCount) slots.push('')
  return slots
}

const toCastTargetSuffix = (
  castId: string,
  resolvedByCastId: Record<string, ResolvedAssignmentEntity[]>,
) => {
  const resolvedTargets = resolvedByCastId[castId] ?? []
  if (!resolvedTargets.length) return ''
  const tokens = toAssignmentEntityTokens(resolvedTargets)
  if (!tokens.length) return ''
  return ` on ${tokens.join(', ')}`
}

const toCastTargetCharacterNames = (
  castId: string,
  resolvedByCastId: Record<string, ResolvedAssignmentEntity[]>,
) => {
  const resolvedTargets = resolvedByCastId[castId] ?? []
  const seenNames = new Set<string>()
  return resolvedTargets.reduce<string[]>((acc, entity) => {
    if (entity.kind !== 'participant' || entity.issue) return acc
    const name = entity.participant.inGameName.trim()
    if (!name.length || seenNames.has(name)) return acc
    seenNames.add(name)
    acc.push(name)
    return acc
  }, [])
}

const getCombinedCascadedReminderTts = (ttsBase: string, indexes: number[]) => {
  if (indexes.length <= 1) return getCascadedReminderTts(ttsBase, indexes[0])
  const ordinals = indexes.map((index) => toOrdinalWord(index + 1))
  return `${ordinals.slice(0, -1).join(', ')} and ${ordinals.at(-1)} ${ttsBase}`
}

const appendTextProperty = (parts: string[], text?: string) => {
  const trimmed = text?.trim()
  if (trimmed) {
    parts.push(`text:${trimmed}`)
  }
}

const appendTtsProperty = (
  parts: string[],
  ttsMessage?: string,
  ...matchingTexts: (string | undefined)[]
) => {
  const trimmed = ttsMessage?.trim()
  if (!trimmed) return
  if (matchingTexts.some((text) => text?.trim() === trimmed)) return
  parts.push(`TTS:${trimmed}`)
}

const parseRgbString = (value?: string) => {
  const trimmed = value?.trim()
  if (!trimmed) return null
  const rgbMatch = trimmed.match(
    /^rgb\(\s*([+-]?\d*\.?\d+)\s*,\s*([+-]?\d*\.?\d+)\s*,\s*([+-]?\d*\.?\d+)\s*\)$/i,
  )
  if (!rgbMatch) return null
  const red = Number(rgbMatch[1])
  const green = Number(rgbMatch[2])
  const blue = Number(rgbMatch[3])
  if (
    !Number.isFinite(red) ||
    !Number.isFinite(green) ||
    !Number.isFinite(blue)
  ) {
    return null
  }
  const normalize = (channel: number, max: number) =>
    Number((Math.max(0, Math.min(channel, max)) / max).toFixed(4))
  return {
    red: normalize(red, 255),
    green: normalize(green, 255),
    blue: normalize(blue, 255),
    alpha: 1,
  }
}

const appendColorsProperty = (parts: string[], rgb?: string) => {
  const parsed = parseRgbString(rgb)
  if (!parsed) return
  parts.push(
    `colors:${parsed.red} ${parsed.green} ${parsed.blue} ${parsed.alpha}`,
  )
}

const appendDurationProperty = (parts: string[], duration?: number) => {
  if (!duration || !Number.isInteger(duration)) return
  parts.push(`dur:${duration}`)
}

const clampTimeToPhase = (at: number, phaseStart: number, phaseEnd: number) => {
  const maxAt = Math.max(phaseStart, Math.ceil(phaseEnd) - 1)
  const rounded = roundToOneDecimal(at)
  return Math.max(phaseStart, Math.min(rounded, maxAt))
}

const appendGlowunitProperty = (parts: string[], characterNames: string[]) => {
  if (!characterNames.length) return
  parts.push(`glowunit:${characterNames.join(' ')}`)
}

const buildInviteList = ({
  activity,
  allEntries,
  selectedParticipants,
}: {
  activity: wowaudit.EventActivity
  allEntries: EventActivityParticipantEntry[]
  selectedParticipants?: ResolvedAssignmentParticipant[]
}) => {
  const selectedParticipantIds = new Set(
    (selectedParticipants ?? []).map(
      (participant) => participant.participantId,
    ),
  )
  const fallbackEntries = allEntries.filter((entry) => {
    if (unavailableParticipantStatuses.includes(entry.participant.status))
      return false
    return entry.selections[activity.id]?.selected === true
  })
  const baseEntries =
    selectedParticipantIds.size > 0
      ? allEntries.filter((entry) =>
          selectedParticipantIds.has(entry.participant.id),
        )
      : fallbackEntries

  return baseEntries
    .map((entry) => resolveEventParticipantCharacter(entry, activity.id))
    .map((character) => ({
      id: character.id,
      name: character.name,
      value: gameVersionHas('blizzard_api')
        ? `${character.inGameName}-${normalizeRealmChatName(
            character.realm.name,
          )}`
        : character.inGameName,
    }))
    .reduce<Array<{ id: number; name: string; value: string }>>((acc, item) => {
      if (acc.some((entry) => entry.id === item.id)) return acc
      acc.push(item)
      return acc
    }, [])
    .sort((left, right) => left.name.localeCompare(right.name))
    .map((item) => item.value)
}

export const buildEventActivityInviteListLine = ({
  activity,
  allEntries,
  selectedParticipants,
}: {
  activity: wowaudit.EventActivity
  allEntries: EventActivityParticipantEntry[]
  selectedParticipants?: ResolvedAssignmentParticipant[]
}) => {
  const inviteList = buildInviteList({
    activity,
    allEntries,
    selectedParticipants,
  })
  return `invitelist:${inviteList.join(' ')};`
}

const buildLinkedRaidPlanNoteParts = (
  raidPlanData: EventActivityLinkedRaidPlanData,
  planAbilityDefaults?: PlanAbilityDefaults,
): {
  timedLines: TimedExportLine[]
  summaryLines: string[]
} => {
  const linkedRaidPlan = raidPlanData.linkedRaidPlan
  const encounterTimeline =
    getEncounterTimelineData()[linkedRaidPlan.encounterId.toString()]
  const encounterDifficulty =
    encounterTimeline?.difficulties.find(
      (difficulty) => difficulty.difficulty === linkedRaidPlan.difficulty,
    ) ?? encounterTimeline?.difficulties[0]
  if (!encounterDifficulty) {
    return { timedLines: [], summaryLines: [] }
  }

  const roundUpToThirtySeconds = (value: number) => Math.ceil(value / 30) * 30
  const encounterMax = encounterDifficulty.maxTime ?? 0
  const maxTime =
    encounterMax > 0
      ? roundUpToThirtySeconds(Math.max(60, encounterMax))
      : roundUpToThirtySeconds(
          Math.max(
            60,
            encounterDifficulty.phases.reduce(
              (sum, phase) => sum + phase.duration,
              0,
            ),
            linkedRaidPlan.timeline.actors.reduce((max, actor) => {
              return actor.casts.reduce(
                (innerMax, cast) =>
                  cast.hidden ? innerMax : Math.max(innerMax, cast.at),
                max,
              )
            }, 0),
            (linkedRaidPlan.timeline.general?.casts ?? []).reduce(
              (max, cast) => (cast.hidden ? max : Math.max(max, cast.at)),
              0,
            ),
          ),
        )
  const { effectivePhaseDurations, phaseStarts, phaseEnds, phaseOrder } =
    resolvePhaseTiming({
      phases: encounterDifficulty.phases,
      persistedPhaseDurations: linkedRaidPlan.timeline.phase_durations,
      phaseOrder: linkedRaidPlan.timeline.phase_order,
    })
  const bossEntries = getBossTimelineEntries({
    maxTime,
    phaseStarts,
    phaseEnds,
    phaseDurations: effectivePhaseDurations,
    abilities: encounterDifficulty.abilities,
  })
  const notePhaseProperty = (phaseIndex: number) =>
    `ph:${formatNotePhaseNumber(
      toNotePhaseNumber(encounterDifficulty.phases, phaseIndex, phaseOrder),
    )}`

  const actorCastLines: TimedExportLine[] = raidPlanData.resolvedActors
    .filter(
      (resolvedActor) =>
        !resolvedActor.issue &&
        resolvedActor.participant &&
        !resolvedActor.isIgnored,
    )
    .flatMap((resolvedActor) => {
      return resolvedActor.actor.casts
        .filter((cast) => !cast.hidden)
        .map((cast) => {
          const phaseIndex = resolvePhaseIndexAtTime(
            cast.at,
            phaseStarts,
            phaseEnds,
            phaseOrder,
          )
          const phaseStart = phaseStarts[phaseIndex] ?? 0
          const relativeTime = Math.max(cast.at - phaseStart, 0)
          const parts = [
            `time:${formatTimeValue(relativeTime)}`,
            notePhaseProperty(phaseIndex),
          ]
          const bossSpellId = getNearestBossSpellId(cast.at, bossEntries)
          if (bossSpellId) {
            parts.push(`bossSpell:${bossSpellId}`)
          }
          parts.push(
            `tag:${
              resolvedActor.participant?.inGameName ?? resolvedActor.actor.label
            }`,
          )
          if (cast.spell_id !== null) {
            parts.push(`spellid:${cast.spell_id}`)
          }
          const shouldAddTargetsToReminderText =
            cast.add_targets_to_reminder_text !== false
          const reminderText = resolveCastReminderText({
            message: cast.message,
            spellId: cast.spell_id,
            defaults: planAbilityDefaults,
          })
          const castMessage = `${reminderText}${
            shouldAddTargetsToReminderText
              ? toCastTargetSuffix(
                  cast.id,
                  raidPlanData.castTargetResolverResult.resolvedByCastId,
                )
              : ''
          }`.trim()
          appendTextProperty(parts, castMessage)
          appendColorsProperty(
            parts,
            resolveCastRgb({
              rgb: cast.rgb,
              spellId: cast.spell_id,
              defaults: planAbilityDefaults,
            }),
          )
          appendTtsProperty(
            parts,
            resolveCastTtsMessage({
              ttsMessage: cast.tts_message,
              spellId: cast.spell_id,
              defaults: planAbilityDefaults,
            }),
            castMessage,
          )
          if (cast.glow_targets_on_raid_frames !== false) {
            appendGlowunitProperty(
              parts,
              toCastTargetCharacterNames(
                cast.id,
                raidPlanData.castTargetResolverResult.resolvedByCastId,
              ),
            )
          }
          if (cast.spell_id === null && !castMessage.length) {
            return null
          }
          return {
            at: cast.at,
            line: `${parts.join(';')};`,
          }
        })
        .filter((entry): entry is TimedExportLine => entry !== null)
    })
  const generalCastLines: TimedExportLine[] = (
    linkedRaidPlan.timeline.general?.casts ?? []
  )
    .filter((cast) => !cast.hidden)
    .map((cast) => {
      const phaseIndex = resolvePhaseIndexAtTime(
        cast.at,
        phaseStarts,
        phaseEnds,
        phaseOrder,
      )
      const phaseStart = phaseStarts[phaseIndex] ?? 0
      const relativeTime = Math.max(cast.at - phaseStart, 0)
      const parts = [
        `time:${formatTimeValue(relativeTime)}`,
        notePhaseProperty(phaseIndex),
      ]
      const bossSpellId = getNearestBossSpellId(cast.at, bossEntries)
      if (bossSpellId) {
        parts.push(`bossSpell:${bossSpellId}`)
      }
      parts.push('tag:everyone')
      if (cast.spell_id !== null) {
        parts.push(`spellid:${cast.spell_id}`)
      }
      const shouldAddTargetsToReminderText =
        cast.add_targets_to_reminder_text !== false
      const reminderText = resolveCastReminderText({
        message: cast.message,
        spellId: cast.spell_id,
        defaults: planAbilityDefaults,
      })
      const castMessage = `${reminderText}${
        shouldAddTargetsToReminderText
          ? toCastTargetSuffix(
              cast.id,
              raidPlanData.castTargetResolverResult.resolvedByCastId,
            )
          : ''
      }`.trim()
      appendTextProperty(parts, castMessage)
      appendColorsProperty(
        parts,
        resolveCastRgb({
          rgb: cast.rgb,
          spellId: cast.spell_id,
          defaults: planAbilityDefaults,
        }),
      )
      appendTtsProperty(
        parts,
        resolveCastTtsMessage({
          ttsMessage: cast.tts_message,
          spellId: cast.spell_id,
          defaults: planAbilityDefaults,
        }),
        castMessage,
      )
      if (cast.glow_targets_on_raid_frames !== false) {
        appendGlowunitProperty(
          parts,
          toCastTargetCharacterNames(
            cast.id,
            raidPlanData.castTargetResolverResult.resolvedByCastId,
          ),
        )
      }
      if (cast.spell_id === null && !castMessage.length) {
        return null
      }
      return {
        at: cast.at,
        line: `${parts.join(';')};`,
      }
    })
    .filter((entry): entry is TimedExportLine => entry !== null)

  const assignmentLines: TimedExportLine[] = []
  const summaryByAbility = new Map<string, AssignmentSummary>()
  const nsrtBlockLinesByAbility = new Map<string, string[]>()

  raidPlanData.linkedPlanAssignments.forEach((normalizedEntry) => {
    const ability = normalizedEntry.ability
    const row = normalizedEntry.row
    const assignmentGroups = getEffectiveAssignmentGroups(
      row.assignmentGroups,
      ability.assignment_groups,
    )
    const abilityName = ability.title?.trim() || row.name
    const defaultAssignmentLabel = `${getSoakPrefix(
      abilityName,
      ability.assignment_type,
      row.isCustom,
    )}${abilityName}`
    const customAbilityMessage = ability.message?.trim()
    const assignmentLabel = customAbilityMessage || defaultAssignmentLabel
    const isGeneralAssignment = isGeneralAssignmentType(
      getEffectiveAssignmentType(ability.assignment_type),
    )
    const viewMode = resolveAssignmentViewMode(
      ability.view_mode,
      row.entries.length,
      ability.assignment_type,
      ability.nsrt_interrupt_module,
      ability.nsrt_transition_module,
    )
    const usesInterruptModule = usesNsrtInterruptModule(
      ability.assignment_type,
      ability.nsrt_interrupt_module,
    )
    const nsrtBlockMarkers = usesInterruptModule
      ? ['intstart', 'intend']
      : usesNsrtTransitionModule(
          ability.assignment_type,
          ability.nsrt_transition_module,
        )
      ? ['transitionStart', 'transitionEnd']
      : null
    const customGrouping = getEffectiveAbilityCustomGrouping(
      ability.spell_id,
      ability.custom_grouping,
    )
    const scopes = getAssignmentScopes(
      row.entries,
      viewMode,
      i18n.t,
      customGrouping,
      ability.assignment_type,
      row.isCustom,
    )
    const useBoxNoteSettings =
      viewMode !== 'all_together' || assignmentGroups > 1
    scopes.forEach((scope) => {
      const representativeCast = normalizeAssignmentCast(
        ability.casts?.[scope.castIndexes[0]],
        assignmentGroups,
      )
      const scopeLabel = isGeneralAssignment
        ? assignmentLabel
        : scopes.length > 1
        ? scope.label
        : ''
      const boxes = Array.from({ length: assignmentGroups }, (_, boxIndex) => {
        const key = `${row.abilityKey}:${scope.id}:${boxIndex}`
        const resolvedEntities =
          raidPlanData.resolverResult.resolvedByScopeBoxKey[key] ?? []
        const box = normalizeAssignmentBox(representativeCast.boxes[boxIndex])
        const tokens = toAssignmentEntityTokens(resolvedEntities)
        const summaryTokens = toAssignmentSummaryTokens(resolvedEntities)
        const characterNames = toAssignmentCharacterNames(resolvedEntities)
        const expected = getEffectiveExpectedEntities(
          ability.expected_entities,
          box.expected_entities,
        )
        const slots = toAssignmentReminderSlots(resolvedEntities, expected)
        return {
          boxIndex,
          tokens,
          summaryTokens,
          characterNames,
          box,
          slots,
        }
      })
      if (
        nsrtBlockMarkers &&
        !nsrtBlockLinesByAbility.has(normalizedEntry.abilityKey)
      ) {
        const lines = boxes
          .map(({ characterNames }) => characterNames.join(' '))
          .filter((line) => line.length > 0)
        if (lines.length > 0) {
          const [blockStart, blockEnd] = nsrtBlockMarkers
          nsrtBlockLinesByAbility.set(normalizedEntry.abilityKey, [
            blockStart,
            ...lines,
            blockEnd,
          ])
        }
      }
      if (!isGeneralAssignment) {
        const abilityCascade = normalizeCascadeReminders(
          ability.cascade_reminders_by,
        )
        const groupCascade = getGroupCascadeReminders(
          assignmentGroups,
          abilityCascade,
        )
        scope.castGroups.forEach((castGroup) => {
          const firstCastIndex = castGroup[0]
          const firstEntry = row.entries[firstCastIndex]
          if (!firstEntry) return
          const castAt = firstEntry.time
          const phaseIndex = firstEntry.phaseIndex
          const phaseStart = phaseStarts[phaseIndex] ?? 0
          const phaseEnd = phaseEnds[phaseIndex] ?? maxTime
          if (usesInterruptModule) {
            boxes.forEach(({ characterNames, box }) => {
              if (!characterNames.length) return
              const boxMessage = useBoxNoteSettings ? box.message?.trim() : ''
              const boxTtsMessage = useBoxNoteSettings
                ? box.tts_message?.trim()
                : ''
              const lineText = boxMessage?.length ? boxMessage : assignmentLabel
              const lineTtsMessage = boxTtsMessage?.length
                ? boxTtsMessage
                : ability.tts_message
              const offset =
                getEffectiveReminderOffset(
                  ability.offset_reminders_by,
                  box.offset_reminders_by,
                ) ?? 0
              const duration = getEffectiveReminderDuration(
                ability.reminder_duration,
                box.reminder_duration,
              )
              const ttsBase = lineTtsMessage?.trim() || lineText
              const positionsByName = characterNames.reduce<
                Map<string, number[]>
              >((acc, name, nameIndex) => {
                acc.set(name, [...(acc.get(name) ?? []), nameIndex])
                return acc
              }, new Map())
              positionsByName.forEach((nameIndexes, name) => {
                const text = `${lineText} (${nameIndexes
                  .map((nameIndex) => `#${nameIndex + 1}`)
                  .join(', ')})`
                const clampedAt = clampTimeToPhase(
                  castAt + offset,
                  phaseStart,
                  phaseEnd,
                )
                const relativeTime = Math.max(clampedAt - phaseStart, 0)
                const parts = [
                  `time:${formatTimeValue(relativeTime)}`,
                  notePhaseProperty(phaseIndex),
                  `tag:${name}`,
                  `text:${text}`,
                ]
                appendDurationProperty(parts, duration)
                appendColorsProperty(parts, box.rgb ?? ability.rgb)
                appendTtsProperty(
                  parts,
                  getCombinedCascadedReminderTts(ttsBase, nameIndexes),
                  text,
                  text === defaultAssignmentLabel ? abilityName : undefined,
                )
                assignmentLines.push({
                  at: clampedAt,
                  line: `${parts.join(';')};`,
                })
              })
            })
            return
          }
          boxes.forEach(({ boxIndex, tokens, box, slots }) => {
            if (!slots.length && !tokens.length) return
            const boxMessage = useBoxNoteSettings ? box.message?.trim() : ''
            const boxTtsMessage = useBoxNoteSettings
              ? box.tts_message?.trim()
              : ''
            const lineText = boxMessage?.length ? boxMessage : assignmentLabel
            const lineTtsMessage = boxTtsMessage?.length
              ? boxTtsMessage
              : ability.tts_message
            const entityCascade = getEntityCascadeReminders(
              assignmentGroups,
              abilityCascade,
              box.cascade_reminders_by,
            )
            const groupOffset = (groupCascade ?? 0) * boxIndex
            const offset =
              getEffectiveReminderOffset(
                ability.offset_reminders_by,
                box.offset_reminders_by,
              ) ?? 0
            const duration = getEffectiveReminderDuration(
              ability.reminder_duration,
              box.reminder_duration,
            )
            const pushLine = (
              lineTokens: string[],
              at: number,
              options?: { text?: string; ttsMessage?: string },
            ) => {
              const clampedAt = clampTimeToPhase(at, phaseStart, phaseEnd)
              const relativeTime = Math.max(clampedAt - phaseStart, 0)
              const text = options?.text ?? lineText
              const ttsMessage = options?.ttsMessage ?? lineTtsMessage
              const parts = [
                `time:${formatTimeValue(relativeTime)}`,
                notePhaseProperty(phaseIndex),
                `tag:${lineTokens.join(' ')}`,
                `text:${text}`,
              ]
              appendDurationProperty(parts, duration)
              appendColorsProperty(parts, box.rgb ?? ability.rgb)
              appendTtsProperty(
                parts,
                ttsMessage,
                text,
                text === defaultAssignmentLabel ? abilityName : undefined,
              )
              assignmentLines.push({
                at: clampedAt,
                line: `${parts.join(';')};`,
              })
            }
            const ttsBase = lineTtsMessage?.trim() || lineText
            if (entityCascade) {
              slots.forEach((token, slotIndex) => {
                if (!token) return
                pushLine(
                  [token],
                  castAt + offset + groupOffset + slotIndex * entityCascade,
                  {
                    text: getCascadedReminderText(lineText, slotIndex),
                    ttsMessage: getCascadedReminderTts(ttsBase, slotIndex),
                  },
                )
              })
            } else if (groupCascade) {
              if (!tokens.length) return
              pushLine(tokens, castAt + offset + groupOffset, {
                text: getCascadedReminderText(lineText, boxIndex),
                ttsMessage: getCascadedReminderTts(ttsBase, boxIndex),
              })
            } else if (tokens.length) {
              pushLine(tokens, castAt + offset)
            }
          })
        })
      }
      if (!nsrtBlockMarkers) {
        boxes.forEach(({ boxIndex, summaryTokens, box }) => {
          if (!summaryTokens.length) return
          const boxMessage = useBoxNoteSettings ? box.message?.trim() : ''
          const abilitySummary = summaryByAbility.get(
            normalizedEntry.abilityKey,
          ) ?? {
            title: abilityName,
            subtitle:
              !isGeneralAssignment &&
              customAbilityMessage &&
              customAbilityMessage !== defaultAssignmentLabel
                ? customAbilityMessage
                : undefined,
            assignmentGroups,
            scopes: [],
          }
          let summaryScope = abilitySummary.scopes.find(
            (entry) => entry.id === scope.id,
          )
          if (!summaryScope) {
            summaryScope = { id: scope.id, label: scopeLabel, groups: [] }
            abilitySummary.scopes.push(summaryScope)
          }
          const defaultGroupName = i18n.t(
            'dashboard.team.plans.assignments.group-label',
            { count: boxIndex + 1 },
          )
          const groupName =
            isGeneralAssignment && boxMessage
              ? boxMessage
              : boxMessage
              ? `${defaultGroupName} (${boxMessage})`
              : defaultGroupName
          const existingGroup = summaryScope.groups.find(
            (group) => group.name === groupName,
          )
          if (existingGroup) {
            summaryTokens.forEach((token) => {
              if (!existingGroup.tokens.includes(token)) {
                existingGroup.tokens.push(token)
              }
            })
          } else {
            summaryScope.groups.push({
              name: groupName,
              tokens: [...summaryTokens],
            })
          }
          summaryByAbility.set(normalizedEntry.abilityKey, abilitySummary)
        })
      }
    })
  })

  const timedLines = [
    ...actorCastLines,
    ...generalCastLines,
    ...assignmentLines,
  ]
  const exportedAbilityKeys = raidPlanData.linkedPlanAssignments
    .map((entry) => entry.abilityKey)
    .filter((abilityKey, index, keys) => {
      if (keys.indexOf(abilityKey) !== index) return false
      return (
        summaryByAbility.has(abilityKey) ||
        nsrtBlockLinesByAbility.has(abilityKey)
      )
    })
  const summaryLines = exportedAbilityKeys.flatMap(
    (abilityKey, abilityIndex) => {
      const abilitySummary = summaryByAbility.get(abilityKey)
      const lines = abilitySummary
        ? formatAssignmentSummaryLines(abilitySummary)
        : []
      const nsrtLines = nsrtBlockLinesByAbility.get(abilityKey)
      if (nsrtLines) {
        if (lines.length > 0) lines.push('')
        lines.push(...nsrtLines)
      }
      if (abilityIndex < exportedAbilityKeys.length - 1) {
        lines.push('')
      }
      return lines
    },
  )

  return { timedLines, summaryLines }
}

export type EventActivityLinkedPlanNoteParts = {
  header: string
  planMessage?: string
  summaryLines: string[]
  timedLines: string[]
  inviteLine: string
}

export const buildEventActivityLinkedPlanNoteParts = ({
  activity,
  linkedPlanData,
  allEntries,
  planAbilityDefaults,
}: {
  activity: wowaudit.EventActivity
  linkedPlanData?: EventActivityLinkedPlanData
  allEntries: EventActivityParticipantEntry[]
  planAbilityDefaults?: PlanAbilityDefaults
}): EventActivityLinkedPlanNoteParts => {
  const encounter = activity.encounter
  const selectedParticipants = linkedPlanData?.selectedParticipants
  const difficultyLabel =
    difficultiesLabels[activity.difficulty] ?? activity.difficulty
  const header = encounter
    ? `EncounterID:${encounter.wclId};Difficulty:${difficultyLabel};Name:${encounter.name}`
    : ''

  const raidPlanParts = (linkedPlanData?.raidPlans ?? []).map((raidPlanData) =>
    buildLinkedRaidPlanNoteParts(raidPlanData, planAbilityDefaults),
  )
  const timedLines = raidPlanParts
    .flatMap((parts) => parts.timedLines)
    .sort((left, right) => {
      if (left.at !== right.at) return left.at - right.at
      return left.line.localeCompare(right.line)
    })
    .map((entry) => entry.line)
  const summaryLines = raidPlanParts.flatMap((parts, index) => {
    if (parts.summaryLines.length === 0) return []
    if (index > 0 && parts.summaryLines[0] !== '') {
      return ['', ...parts.summaryLines]
    }
    return parts.summaryLines
  })

  const planMessage = (linkedPlanData?.raidPlans ?? [])
    .filter(
      (raidPlanData) => raidPlanData.linkedRaidPlan.includeMessageInExport,
    )
    .map((raidPlanData) => htmlToPlainText(raidPlanData.linkedRaidPlan.message))
    .filter((text) => text.length > 0)
    .join('\n\n')

  return {
    header,
    planMessage: planMessage || undefined,
    summaryLines,
    timedLines,
    inviteLine: buildEventActivityInviteListLine({
      activity,
      allEntries,
      selectedParticipants,
    }),
  }
}

export const joinEventActivityLinkedPlanNote = (
  parts: EventActivityLinkedPlanNoteParts,
) => {
  const noteChunks: string[] = []
  if (parts.header) {
    noteChunks.push(parts.header)
  }
  if (parts.planMessage) {
    if (noteChunks.length > 0) noteChunks.push('')
    noteChunks.push(parts.planMessage)
  }
  if (parts.summaryLines.length > 0) {
    if (noteChunks.length > 0) noteChunks.push('')
    noteChunks.push(...parts.summaryLines.map(formatNoteMarkerIcons))
  }
  if (parts.timedLines.length > 0) {
    if (noteChunks.length > 0) noteChunks.push('')
    noteChunks.push(...parts.timedLines)
  }
  if (parts.inviteLine.length > 0) {
    if (noteChunks.length > 0) noteChunks.push('')
    noteChunks.push(parts.inviteLine)
  }
  return noteChunks.join('\n')
}

export const buildEventActivityLinkedPlanNote = ({
  activity,
  linkedPlanData,
  allEntries,
  planAbilityDefaults,
}: {
  activity: wowaudit.EventActivity
  linkedPlanData?: EventActivityLinkedPlanData
  allEntries: EventActivityParticipantEntry[]
  planAbilityDefaults?: PlanAbilityDefaults
}) =>
  joinEventActivityLinkedPlanNote(
    buildEventActivityLinkedPlanNoteParts({
      activity,
      linkedPlanData,
      allEntries,
      planAbilityDefaults,
    }),
  )
